import { faUndoAlt } from '@fortawesome/pro-solid-svg-icons';
import { Box, Modal, TableCell, TableRow } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { PageResult, Period } from '@spike/model';
import { cleanInvoice } from '@spike/invoices-action';
import { showError } from '@spike/notifications-action';
import {
    fetchThunk as fetchPaymentsThunk,
    fetchRefundsThunk,
    fetchTransactionsThunk,
    getPaymentThunk,
    hasPaymentsThunk,
    PaymentsStatus
} from '@spike/payments-action';
import { Payment, Refund as RefundModel, Transaction, TransactionTypes } from '@spike/payments-model';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import Checkout from 'components/Checkout2/Checkout';
import { Spinner, ToolbarButton, ToolbarItem } from 'components/UI';
import moment, { Moment } from 'moment-timezone';
import { Fragment, FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import Table from '../Table';
import { useCommonStyles as useTableStyles } from '../Table/CommonStyles';
import { All, Paid } from './Categories';
import InvoiceDetail from './Invoices/InvoiceDetail';
import PaymentMethod from './PaymentMethod';
import ViewReceipt from './ReceiptView';
import Refund from './Refund';
import State from './State';
import TransactionsEmpty from './TransactionsEmpty';
import TransactionsHeader from './TransactionsHeader';

import { faLocationDot } from '@fortawesome/pro-light-svg-icons/faLocationDot';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ApiClientWrapper } from '@spike/api-client';
import { useApiClientWrapper, usePermissions } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';

interface TransactionsProps {
    className?: string;
    clientId?: number;
    emptyState?: ReactNode;
    /** Used when opening Transactions from Commissions "Refunded" without changing the URL. */
    initialCategoryId?: string;
    /** Used to filter transactions to a specific date. */
    initialDate?: Moment;
    /** Used to filter transactions to specific marketplaces. */
    initialMarketplaceIds?: number[];
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            width: '100%',
            flexDirection: 'column'
        },
        loadingContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            backgroundColor: '#FAFAFA'
        },
        modal: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        alignRight: {
            textAlign: 'right'
        },
        locationContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            color: '#7A7A7A',
            fontSize: 14,
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '100%',
            marginTop: 7
        },
        locationIcon: {
            color: '#7A7A7A',
            height: 16,
            width: 16
        },
        invoiceLink: {
            'all': 'unset',
            'color': '#000000',
            'fontWeight': 700,
            'textDecoration': 'underline',
            'cursor': 'pointer',
            '&:hover': {
                opacity: 0.75
            }
        }
    })
);

interface SelectedInvoiceDetail {
    invoiceId: number;
    marketplaceId: number;
    marketplaceName: string;
}

const formatAmount = (amount: string | number): string =>
    Number(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

const fetch = (
    apiClientWrapper: ApiClientWrapper,
    dispatch: any,
    marketplaceIds: Array<number>,
    categoryId: string,
    clientId: number | null,
    period: Period | null,
    page?: number,
    pageSize?: number
) => {
    if (categoryId === Paid.id) {
        dispatch(fetchPaymentsThunk(apiClientWrapper, marketplaceIds, clientId, period, page || 1, pageSize || 10));
    } else if (categoryId === All.id) {
        dispatch(fetchTransactionsThunk(apiClientWrapper, marketplaceIds, clientId, period, page || 1, pageSize || 10));
    } else {
        dispatch(fetchRefundsThunk(apiClientWrapper, marketplaceIds, clientId, period, page || 1, pageSize || 10));
    }
};

// Accounting format: negatives (refunds) go in parentheses.
const formatSignedAmount = (amount: string | number): string =>
    Number(amount) < 0 ? `($${formatAmount(Math.abs(Number(amount)))})` : `$${formatAmount(amount)}`;

export const Transactions: FunctionComponent<TransactionsProps> = props => {
    const classes = useStyles();
    const tableStyles = useTableStyles();
    const isClientScoped = props.clientId !== undefined;

    const permissions = usePermissions();
    const refundsEnabled = permissions.checkout.refunds;

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const { organizationMarketplaces, isUserLoggedInMarketplace, getOrganizationMarketplace } =
        useOrganizationMarketplaces();

    const payments = useSelector<RootState, PageResult<Payment>>(state => state.payments.payments);
    const totalPayments = useSelector<RootState, string | null>(state => state.payments.payments.total);
    const hasPayments = useSelector<RootState, boolean>(state => state.payments.hasPayments);
    const refunds = useSelector<RootState, PageResult<RefundModel>>(state => state.payments.refunds);
    const totalRefunds = useSelector<RootState, string | null>(state => state.payments.refunds.total);
    const transactions = useSelector<RootState, PageResult<Transaction>>(state => state.payments.transactions);
    const totalTransactions = useSelector<RootState, string | null>(state => state.payments.transactions.total);

    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);
    const loadedPayment = useSelector<RootState, Payment | undefined>(state => state.payments.payment);

    const [loadingHasPayments, setLoadingHasPayments] = useState(!isClientScoped);
    const [loading, setLoading] = useState(true);
    const [refundPaymentId, setRefundPaymentId] = useState<number | null>(null);
    const [viewReceipt, setViewReceipt] = useState<Payment | null>(null);
    const [openingReceipt, setOpeningReceipt] = useState(false);
    const [selectedInvoiceDetail, setSelectedInvoiceDetail] = useState<SelectedInvoiceDetail | null>(null);
    const [showCheckout, setShowCheckout] = useState<{
        orderId?: number;
        bookingId?: number;
        hide: boolean;
    } | null>(null);
    const returnToInvoiceDetailRef = useRef<SelectedInvoiceDetail | null>(null);
    const isCheckoutOpen = showCheckout !== null;

    const [period, setPeriod] = useState<Period>(() => {
        if (props.initialDate) {
            return { from: moment(props.initialDate.clone()), to: moment(props.initialDate.clone()) };
        }
        return { from: moment(), to: moment() };
    });
    const [clientId, setClientId] = useState<number | null>(props.clientId ?? null);
    const [categoryId, setCategoryId] = useState(() => props.initialCategoryId ?? All.id);
    const [optionSelected, setOptionSelected] = useState<string | undefined>(props.initialDate ? undefined : 'Today');
    const [marketplaceIds, setMarketplaceIds] = useState<Array<number>>(
        props.initialMarketplaceIds ?? organizationMarketplaces.map(m => m.id)
    );

    const currentPagination =
        categoryId === Paid.id
            ? payments.pagination
            : categoryId === All.id
            ? transactions.pagination
            : refunds.pagination;

    useEffect(() => {
        if (isClientScoped) {
            fetch(apiClientWrapper, dispatch, marketplaceIds, categoryId, props.clientId ?? null, period, 1, 10);
            return;
        }

        dispatch(hasPaymentsThunk(apiClientWrapper, marketplaceIds));
    }, []);

    useEffect(() => {
        if (props.clientId !== undefined) {
            setClientId(props.clientId);
        }
    }, [props.clientId]);

    useNonInitialEffect(() => {
        switch (paymentsStatus) {
            case PaymentsStatus.HasPaymentsSuccess:
                fetch(
                    apiClientWrapper,
                    dispatch,
                    marketplaceIds,
                    categoryId,
                    isClientScoped ? props.clientId ?? null : clientId,
                    period,
                    1,
                    10
                );
                setLoadingHasPayments(false);
                break;
            case PaymentsStatus.FetchSuccess:
                setLoading(false);
                break;
            case PaymentsStatus.FetchRefundsSuccess:
                setLoading(false);
                break;
            case PaymentsStatus.FetchTransactionsSuccess:
                setLoading(false);
                break;
            case PaymentsStatus.GetPaymentSuccess:
                if (openingReceipt && loadedPayment) {
                    setViewReceipt(loadedPayment);
                }
                setOpeningReceipt(false);
                break;
            case PaymentsStatus.Error:
                setLoadingHasPayments(false);
                setLoading(false);
                setOpeningReceipt(false);
                break;
        }
    }, [paymentsStatus]);

    const changePageHandler = (page: number) => {
        setLoading(true);
        fetch(
            apiClientWrapper,
            dispatch,
            marketplaceIds,
            categoryId,
            clientId,
            period,
            page,
            currentPagination?.pageSize
        );
    };

    const changePageSizeHandler = (pageSize: number) => {
        setLoading(true);
        fetch(
            apiClientWrapper,
            dispatch,
            marketplaceIds,
            categoryId,
            clientId,
            period,
            currentPagination?.page,
            pageSize
        );
    };

    const changeCategoryHandler = (categoryId: string) => {
        setLoading(true);
        setCategoryId(categoryId);
        fetch(
            apiClientWrapper,
            dispatch,
            marketplaceIds,
            categoryId,
            clientId,
            period,
            currentPagination?.page,
            currentPagination?.pageSize
        );
    };

    const changePeriodHandler = (period: Period, opSelect?: string) => {
        setLoading(true);
        setPeriod(period);
        setOptionSelected(opSelect);
        fetch(
            apiClientWrapper,
            dispatch,
            marketplaceIds,
            categoryId,
            clientId,
            period,
            currentPagination?.page,
            currentPagination?.pageSize
        );
    };

    const changeClientHandler = (selectedClientId: number | null) => {
        if (clientId !== selectedClientId) {
            setLoading(true);
            setClientId(selectedClientId);
            fetch(
                apiClientWrapper,
                dispatch,
                marketplaceIds,
                categoryId,
                selectedClientId,
                period,
                1,
                currentPagination?.pageSize
            );
        }
    };

    const changeMarketplaceIdsHandler = (marketplaceIds: Array<number>) => {
        setLoading(true);
        setMarketplaceIds(marketplaceIds);

        if (isClientScoped) {
            fetch(
                apiClientWrapper,
                dispatch,
                marketplaceIds,
                categoryId,
                props.clientId ?? null,
                period,
                1,
                currentPagination?.pageSize
            );
            return;
        }

        dispatch(hasPaymentsThunk(apiClientWrapper, marketplaceIds));
    };

    const refreshTransactions = () => {
        setLoading(true);
        fetch(
            apiClientWrapper,
            dispatch,
            marketplaceIds,
            categoryId,
            clientId,
            period,
            currentPagination?.page,
            currentPagination?.pageSize
        );
    };

    const openInvoiceDetailHandler = (invoiceId: number | undefined, marketplaceId: number) => {
        if (!invoiceId) {
            return;
        }

        setSelectedInvoiceDetail({
            invoiceId,
            marketplaceId,
            marketplaceName: getOrganizationMarketplace(marketplaceId)?.name ?? ''
        });
    };

    const closeInvoiceDetailHandler = () => {
        returnToInvoiceDetailRef.current = null;
        setSelectedInvoiceDetail(null);
        dispatch(cleanInvoice());
        refreshTransactions();
    };

    const checkoutHandler = (orderId: number | undefined, bookingId: number | undefined) => {
        if (isCheckoutOpen) {
            return;
        }

        if (!orderId && !bookingId) {
            dispatch(showError('Unable to open checkout for this invoice.'));
            return;
        }

        setShowCheckout({
            orderId: orderId ?? undefined,
            bookingId: bookingId ?? undefined,
            hide: false
        });
    };

    const closeCheckoutHandler = () => {
        const returnToInvoiceDetail = returnToInvoiceDetailRef.current;
        returnToInvoiceDetailRef.current = null;
        setShowCheckout(null);
        refreshTransactions();

        if (returnToInvoiceDetail) {
            setSelectedInvoiceDetail(returnToInvoiceDetail);
        }
    };

    const checkoutFromInvoiceDetailHandler = (bookingId: number | undefined, orderId: number | undefined) => {
        if (!selectedInvoiceDetail) {
            return;
        }

        returnToInvoiceDetailRef.current = selectedInvoiceDetail;
        setSelectedInvoiceDetail(null);
        dispatch(cleanInvoice());
        checkoutHandler(orderId, bookingId);
    };

    const renderInvoiceNumber = (
        invoiceId: number | undefined,
        invoiceNumber: string | number,
        marketplaceId: number
    ) => {
        if (!invoiceId) {
            return invoiceNumber;
        }

        return (
            <button
                type="button"
                className={classes.invoiceLink}
                onClick={event => {
                    event.stopPropagation();
                    openInvoiceDetailHandler(invoiceId, marketplaceId);
                }}
            >
                {invoiceNumber}
            </button>
        );
    };

    const paidHeaders = (
        <>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}>Date</TableCell>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}>Invoice</TableCell>
            {!isClientScoped && (
                <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}>Client</TableCell>
            )}
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}>Paid Type</TableCell>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell, classes.alignRight)}>
                Amount
            </TableCell>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}>Status</TableCell>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}></TableCell>
        </>
    );

    const refundHeaders = (
        <>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}>Date of Refund</TableCell>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}>Invoice</TableCell>
            {!isClientScoped && (
                <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}>Client</TableCell>
            )}
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell, classes.alignRight)}>
                Amount
            </TableCell>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}>Note</TableCell>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}>Status</TableCell>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}></TableCell>
        </>
    );

    const allHeaders = (
        <>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}>Date</TableCell>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}>Invoice</TableCell>
            {!isClientScoped && (
                <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}>Client</TableCell>
            )}
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}>Type</TableCell>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}>Paid Type</TableCell>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell, classes.alignRight)}>
                Amount
            </TableCell>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.darkCell)}>Status</TableCell>
        </>
    );

    const getLocation = (marketplaceId: number) =>
        organizationMarketplaces.length > 1 ? (
            <div className={classes.locationContainer}>
                <FontAwesomeIcon
                    icon={faLocationDot}
                    className={classes.locationIcon}
                />
                {getOrganizationMarketplace(marketplaceId)?.name}
            </div>
        ) : null;

    const paidRows = (
        <>
            {payments.page.map(payment => (
                <TableRow
                    key={payment.uuid}
                    onClick={() => setViewReceipt(payment)}
                    style={{ cursor: 'pointer' }}
                >
                    <TableCell className={tableStyles.cell}>
                        {payment.paidAt.format('MM/DD/YYYY, h:mm a')}
                        {getLocation(payment.marketplaceId)}
                    </TableCell>

                    <TableCell className={tableStyles.cell}>
                        {renderInvoiceNumber(payment.invoice.id, payment.invoice.number, payment.marketplaceId)}
                    </TableCell>

                    {!isClientScoped && <TableCell className={tableStyles.cell}>{payment.customer.name}</TableCell>}

                    <TableCell className={tableStyles.cell}>
                        <PaymentMethod
                            paymentMethodId={payment.paymentMethod.id}
                            paymentMethodName={payment.paymentMethod.name}
                            cardDigits={payment.paymentMethod.cardDigits}
                        />
                    </TableCell>

                    <TableCell className={clsx(tableStyles.cell, classes.alignRight)}>
                        {`$${formatAmount(payment.amount)}`}
                    </TableCell>

                    <TableCell className={tableStyles.cell}>
                        <State
                            state={payment.status}
                            type="payment"
                        />
                    </TableCell>

                    <TableCell className={tableStyles.cell}>
                        {isUserLoggedInMarketplace(payment.marketplaceId) && payment.refundable && refundsEnabled && (
                            <ToolbarButton>
                                <ToolbarItem
                                    text="Refund"
                                    icon={faUndoAlt}
                                    onClick={() => {
                                        setRefundPaymentId(payment.id);
                                    }}
                                />
                            </ToolbarButton>
                        )}
                    </TableCell>
                </TableRow>
            ))}
        </>
    );

    // Refund rows are intentionally not clickable: only payments open the receipt.
    const openPaymentReceipt = (transaction: Transaction) => {
        setOpeningReceipt(true);
        dispatch(getPaymentThunk(apiClientWrapper, transaction.paymentId));
    };

    const allRows = (
        <Fragment>
            {transactions.page.map(transaction => (
                <TableRow
                    key={`${transaction.type}-${transaction.uuid}`}
                    onClick={
                        transaction.type === TransactionTypes.Payment
                            ? () => openPaymentReceipt(transaction)
                            : undefined
                    }
                    style={transaction.type === TransactionTypes.Payment ? { cursor: 'pointer' } : undefined}
                >
                    <TableCell className={tableStyles.cell}>
                        {transaction.date.format('MM/DD/YYYY, h:mm a')}
                        {getLocation(transaction.marketplaceId)}
                    </TableCell>
                    <TableCell className={tableStyles.cell}>
                        {renderInvoiceNumber(
                            transaction.invoice.id,
                            transaction.invoice.number,
                            transaction.marketplaceId
                        )}
                    </TableCell>
                    {!isClientScoped && (
                        <TableCell className={tableStyles.cell}>
                            {`${transaction.customer.firstName} ${transaction.customer.lastName}`}
                        </TableCell>
                    )}
                    <TableCell className={tableStyles.cell}>
                        {transaction.type === TransactionTypes.Refund ? 'Refund' : 'Payment'}
                    </TableCell>
                    <TableCell className={tableStyles.cell}>
                        <PaymentMethod
                            paymentMethodId={transaction.paymentMethod.id}
                            paymentMethodName={transaction.paymentMethod.name}
                            cardDigits={transaction.paymentMethod.cardDigits}
                        />
                    </TableCell>
                    <TableCell className={clsx(tableStyles.cell, classes.alignRight)}>
                        {formatSignedAmount(transaction.amount)}
                    </TableCell>
                    <TableCell className={tableStyles.cell}>
                        <State
                            state={transaction.status}
                            type={transaction.type === TransactionTypes.Refund ? 'refund' : 'payment'}
                        />
                    </TableCell>
                </TableRow>
            ))}
        </Fragment>
    );

    const refundRows = (
        <Fragment>
            {refunds.page.map(refund => (
                <TableRow key={refund.uuid}>
                    <TableCell className={tableStyles.cell}>
                        {refund.refundDate.format('MM/DD/YYYY, h:mm a')}
                        {getLocation(refund.marketplaceId)}
                    </TableCell>
                    <TableCell className={tableStyles.cell}>
                        {renderInvoiceNumber(refund.invoice.id, refund.invoice.number, refund.marketplaceId)}
                    </TableCell>
                    {!isClientScoped && (
                        <TableCell className={tableStyles.cell}>
                            {`${refund.customer.firstName} ${refund.customer.lastName}`}
                        </TableCell>
                    )}
                    <TableCell className={clsx(tableStyles.cell, classes.alignRight)}>
                        {formatSignedAmount(-Number(refund.amount))}
                    </TableCell>

                    <TableCell className={tableStyles.cell}>{refund.reason || '-'}</TableCell>
                    <TableCell className={tableStyles.cell}>
                        <State
                            state={refund.status}
                            type="refund"
                        />
                    </TableCell>
                </TableRow>
            ))}
        </Fragment>
    );

    const currentPageItems =
        categoryId === Paid.id ? payments.page : categoryId === All.id ? transactions.page : refunds.page;
    const isCurrentListEmpty =
        !loading && (currentPagination?.totalRecords || 0) === 0 && currentPageItems.length === 0;

    return (
        <>
            {loadingHasPayments && (
                <Box className={classes.loadingContainer}>
                    <Spinner />
                </Box>
            )}

            {!loadingHasPayments && !isClientScoped && !hasPayments && <TransactionsEmpty />}

            {!loadingHasPayments && (isClientScoped || hasPayments) && (
                <Box className={clsx(classes.container, props.className)}>
                    <TransactionsHeader
                        categoryId={categoryId}
                        clientId={clientId}
                        hideClientFilter={isClientScoped}
                        period={period!}
                        optionSelected={optionSelected}
                        initialMarketplaceIds={marketplaceIds}
                        onChangeCategory={changeCategoryHandler}
                        onChangePeriod={changePeriodHandler}
                        onChangeClient={changeClientHandler}
                        onChangeMarketplaceIds={changeMarketplaceIdsHandler}
                        totalAmount={
                            categoryId === Paid.id
                                ? totalPayments || '0.00'
                                : categoryId === All.id
                                ? totalTransactions || '0.00'
                                : String(-Number(totalRefunds || 0))
                        }
                        totalElements={loading ? undefined : currentPagination?.totalRecords}
                    />
                    {isClientScoped && isCurrentListEmpty && props.emptyState ? (
                        props.emptyState
                    ) : (
                        <Table
                            headers={
                                categoryId === Paid.id
                                    ? paidHeaders
                                    : categoryId === All.id
                                    ? allHeaders
                                    : refundHeaders
                            }
                            rows={categoryId === Paid.id ? paidRows : categoryId === All.id ? allRows : refundRows}
                            pagination={{ ...currentPagination! }}
                            loading={loading}
                            onChangePage={changePageHandler}
                            onChangePageSize={changePageSizeHandler}
                        />
                    )}
                </Box>
            )}

            {viewReceipt !== null && (
                <ViewReceipt
                    payment={viewReceipt}
                    onBack={() => setViewReceipt(null)}
                />
            )}

            {showCheckout && (
                <Checkout
                    orderId={showCheckout.orderId}
                    bookingId={showCheckout.bookingId}
                    onClose={closeCheckoutHandler}
                    onPayByLinkSent={closeCheckoutHandler}
                    onBack={closeCheckoutHandler}
                    hide={showCheckout.hide}
                    zIndex={props.clientId ? 10100 : undefined}
                />
            )}

            {selectedInvoiceDetail && !isCheckoutOpen && (
                <InvoiceDetail
                    invoiceId={selectedInvoiceDetail.invoiceId}
                    marketplaceId={selectedInvoiceDetail.marketplaceId}
                    marketplaceName={selectedInvoiceDetail.marketplaceName}
                    onClose={closeInvoiceDetailHandler}
                    onCheckout={checkoutFromInvoiceDetailHandler}
                    zIndex={props.clientId ? 10100 : undefined}
                />
            )}

            {refundPaymentId !== null && (
                <Modal
                    open={true}
                    aria-labelledby="simple-dialog-title"
                    className={classes.modal}
                >
                    <Refund
                        paymentId={refundPaymentId}
                        onClose={() => setRefundPaymentId(null)}
                        onRefunded={() => setRefundPaymentId(null)}
                    />
                </Modal>
            )}
        </>
    );
};

export default Transactions;
